import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

export const FeedTypeEnum = z.enum(['PLAIN', 'FORTIFIED']);
export type FeedType = z.infer<typeof FeedTypeEnum>;

export const ExpiryTypeEnum = z.enum(['MILK', 'FEED', 'FORMULA']);
export type ExpiryType = z.infer<typeof ExpiryTypeEnum>;

export const MilkExpiryRuleSchema = z.object({
  rule_id:           uuidSchema,
  hospital_id:       uuidSchema.nullable(),
  category:          z.string(),
  milk_type_label:   z.string().nullable(),//z.enum(['FRESH', 'FROZEN', 'THAWED', 'PASTEURISED', 'OPEN']),
  storage_condition: z.string().nullable(),
  expiry_duration:   z.number().int().nullable(),
  unit:              z.enum(['HOURS', 'DAYS', 'MONTHS']).nullable(),
  feed_type:         FeedTypeEnum.nullable(),
  expiry_type:       ExpiryTypeEnum.nullable(),
  form_factor:       z.string().nullish(),
  state:             z.string().nullish(),
  expiry_logic:      z.string().nullish(),
  is_active:         z.boolean(),
  created_by:        uuidSchema.nullable(),
  updated_by:        uuidSchema.nullable(),
  created_at:        z.string(),
  updated_at:        z.string(),
});

export const CreateMilkExpiryRuleSchema = z.object({
  hospital_id:       uuidSchema,
  category:          z.enum(['MOTHER', 'DONOR', 'FORMULA']),
  milk_type_label:   z.enum(['FRESH', 'FROZEN', 'THAWED', 'PASTEURISED', 'OPEN']).nullable().optional(),
  storage_condition: z.enum(['REFRIGERATED', 'FROZEN']).optional(),
  expiry_duration:   z.number().int().positive().nullable().optional(),
  unit:              z.enum(['HOURS', 'DAYS', 'MONTHS']).nullable().optional(),
  feed_type:         FeedTypeEnum.nullable().optional(),
  expiry_type:       ExpiryTypeEnum.optional(),
  form_factor:       z.string().nullable().optional(),
  state:             z.string().nullable().optional(),
  expiry_logic:      z.string().nullable().optional(),
  is_active:         z.boolean().default(true),
  created_by:        uuidSchema
}).superRefine((data, ctx) => {
  if ((data.expiry_type === 'MILK' || data.expiry_type === 'FORMULA') && data.feed_type != null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['feed_type'],
      message: `feed_type must be null when expiry_type is ${data.expiry_type}.`,
    });
  }
  if (data.expiry_type === 'FEED' && data.feed_type == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['feed_type'],
      message: 'feed_type is required when expiry_type is FEED.',
    });
  }
});

export const UpdateMilkExpiryRuleSchema = z.object({
  category:          z.enum(['MOTHER', 'DONOR', 'FORMULA']).optional(),
  milk_type_label:   z.enum(['FRESH', 'FROZEN', 'THAWED', 'PASTEURISED', 'OPEN']).nullable().optional(),
  storage_condition: z.enum(['REFRIGERATED', 'FROZEN']).nullable().optional(),
  expiry_duration:   z.number().int().positive().nullable().optional(),
  unit:              z.enum(['HOURS', 'DAYS', 'MONTHS']).nullable().optional(),
  feed_type:         FeedTypeEnum.nullable().optional(),
  expiry_type:       ExpiryTypeEnum.nullable().optional(),
  form_factor:       z.string().nullable().optional(),
  state:             z.string().nullable().optional(),
  expiry_logic:      z.string().nullable().optional(),
  is_active:         z.boolean().optional(),
  updated_by:        uuidSchema,
}).superRefine((data, ctx) => {
  if ((data.expiry_type === 'MILK' || data.expiry_type === 'FORMULA') && data.feed_type != null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['feed_type'],
      message: `feed_type must be null when expiry_type is ${data.expiry_type}.`,
    });
  }
  if (data.expiry_type === 'FEED' && data.feed_type == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['feed_type'],
      message: 'feed_type is required when expiry_type is FEED.',
    });
  }
});

export const MilkExpiryRuleResponseSchema = MilkExpiryRuleSchema.extend({
  created_at: z.coerce.date().transform((d) => d.toISOString()),
  updated_at: z.coerce.date().transform((d) => d.toISOString()),
});

export type MilkExpiryRule = z.infer<typeof MilkExpiryRuleSchema>;
export type CreateMilkExpiryRule = z.infer<typeof CreateMilkExpiryRuleSchema>;
export type UpdateMilkExpiryRule = z.infer<typeof UpdateMilkExpiryRuleSchema>;
export type MilkExpiryRuleResponse = z.infer<typeof MilkExpiryRuleResponseSchema>;
